import { LoggerNames, LogLevel } from './constants';
import { AuthSdkError } from './auth-sdk-error';

type LoggerStyle = {
  bgColor?: string;
  textColor?: string;
};
type LoggerFn = (...args: unknown[]) => void;
// only create one empty function reference
export const voidFn: LoggerFn = () => {};

// styling for logger prefix
const defaultBgColor = `rgb(13, 41, 110)`;
const defaultTextColor = `#efefef`;

export class Logger {
  private readonly level: LogLevel;
  // Bind to window.console to preserve context and line numbers
  public readonly debug: LoggerFn;
  public readonly info: LoggerFn;
  public readonly warn: LoggerFn;
  public readonly error: LoggerFn;

  constructor(
    name: LoggerNames = LoggerNames.MAP_SSO,
    level: LogLevel = LogLevel.ERROR,
    { bgColor = defaultBgColor, textColor = defaultTextColor }: LoggerStyle = {},
  ) {
    this.level = level;
    const prefix = `%c${name}`;
    const prefixStyle = `background-color: ${bgColor}; color: ${textColor}; font-weight: bold ; padding: 6px; border-radius: 25%;`;
    // We bind the logging functions so that they maintain their context and line numbers in the console
    this.debug = this.level <= LogLevel.DEBUG ? console.debug.bind(window.console, prefix, prefixStyle) : voidFn;
    this.info = this.level <= LogLevel.INFO ? console.info.bind(window.console, prefix, prefixStyle) : voidFn;
    this.warn = this.level <= LogLevel.WARN ? console.warn.bind(window.console, prefix, prefixStyle) : voidFn;
    this.error = this.level <= LogLevel.ERROR ? console.error.bind(window.console, prefix, prefixStyle) : voidFn;
  }

  private static logger: Logger;

  public static GlobalLogLevel: LogLevel = LogLevel.ERROR;

  public static initLogger(name: LoggerNames, level: LogLevel, style?: LoggerStyle) {
    this.GlobalLogLevel = level;
    this.logger = new Logger(name, level, style);
    return this.logger;
  }

  public static getLogger(): Logger {
    if (!this.logger) {
      throw new AuthSdkError('Logger not initialized');
    }

    return this.logger;
  }

  public static createLogger(name: LoggerNames, level = this.GlobalLogLevel, style?: LoggerStyle) {
    return new Logger(name, level, style);
  }
}
